import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { X, Search, Loader2, ChevronDown } from 'lucide-react';
import { apiRequest } from '../../config/api';

function RecordSearchField({ objectDefinitionId, value, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const triggerRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState(null);

  const search = useCallback(async (q) => {
    if (!objectDefinitionId) return;
    setLoading(true);
    try {
      const res = await apiRequest(`/crm/objects/${objectDefinitionId}/records-search?q=${encodeURIComponent(q)}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setOptions(data);
      }
    } catch (e) {
      console.error('Error searching records:', e);
    } finally {
      setLoading(false);
    }
  }, [objectDefinitionId]);

  // Fetch on open
  useEffect(() => {
    if (open) search(query);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search on query change
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => search(query), 250);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  // Resolve label for initial value
  useEffect(() => {
    if (value && !selectedLabel && objectDefinitionId) {
      apiRequest(`/crm/objects/${objectDefinitionId}/records-search?q=&limit=50`)
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          const match = data.find(o => o.id === value);
          if (match) setSelectedLabel(match.label);
        })
        .catch(() => {});
    }
  }, [value, selectedLabel, objectDefinitionId]);

  // Position dropdown via portal
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          !e.target.closest('[data-record-dropdown]')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const dropdown = open && dropdownPos && ReactDOM.createPortal(
    <div
      data-record-dropdown
      className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl dark:bg-gray-800 dark:border-gray-600"
      style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
    >
      <div className="p-2 border-b border-gray-100 dark:border-gray-700">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-200 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-brand-500"
            ref={(el) => { if (el) requestAnimationFrame(() => el.focus()); }}
          />
        </div>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
          </div>
        ) : options.length === 0 ? (
          <div className="px-3 py-4 text-sm text-gray-500 text-center">No records found</div>
        ) : (
          options.map((opt) => (
            <div
              key={opt.id}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${opt.id === value ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300' : 'text-gray-700 dark:text-gray-300'}`}
              onClick={() => {
                onChange(opt.id);
                setSelectedLabel(opt.label);
                setOpen(false);
                setQuery('');
              }}
            >
              {opt.label}
            </div>
          ))
        )}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <div
        ref={triggerRef}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer flex items-center justify-between dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
        onClick={() => setOpen(!open)}
      >
        <span className={selectedLabel ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}>
          {selectedLabel || placeholder || 'Select...'}
        </span>
        <div className="flex items-center gap-1">
          {value && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onChange(''); setSelectedLabel(''); }}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-3 h-3" />
            </button>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
        </div>
      </div>
      {dropdown}
    </>
  );
}

function UserSearchField({ value, onChange, placeholder }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiRequest('/organizations/current/members')
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (!cancelled) setUsers(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400 inline" /> Loading...
      </div>
    );
  }

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200"
    >
      <option value="">{placeholder || 'Select user...'}</option>
      {users.map((u) => (
        <option key={u.id} value={u.id}>
          {u.full_name || u.email}
        </option>
      ))}
    </select>
  );
}

function CreateRecordModal({ isOpen, onClose, onCreated, objectDefinitionId, fieldDefinitions = [], defaultValues = {}, hiddenFields = [] }) {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({ ...defaultValues });
      setError('');
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (slug, value) => {
    setFormData((prev) => ({ ...prev, [slug]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    for (const fd of fieldDefinitions) {
      if (fd.is_required && !formData[fd.slug]) {
        setError(`${fd.name} is required`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await apiRequest(`/crm/objects/${objectDefinitionId}/records/`, {
        method: 'POST',
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        const data = await res.json();
        onCreated?.(data);
        onClose();
      } else {
        const err = await res.json().catch(() => null);
        setError(err?.detail || 'Failed to create record');
      }
    } catch (err) {
      setError('Failed to create record');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const hiddenSet = new Set(hiddenFields);
  const sortedFields = [...fieldDefinitions]
    .filter((fd) => !hiddenSet.has(fd.slug))
    .sort((a, b) => a.sort_order - b.sort_order);
  const useWideLayout = sortedFields.length > 5;

  const renderField = (fd) => {
    const value = formData[fd.slug] ?? '';
    const baseClasses = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200";

    switch (fd.field_type) {
      case 'text':
      case 'url':
        return (
          <input
            type={fd.field_type === 'url' ? 'url' : 'text'}
            value={value}
            onChange={(e) => handleChange(fd.slug, e.target.value)}
            placeholder={fd.name}
            className={baseClasses}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(fd.slug, e.target.value ? Number(e.target.value) : '')}
            placeholder={fd.name}
            className={baseClasses}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(fd.slug, e.target.value)}
            className={baseClasses}
          />
        );
      case 'boolean':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!value}
              onChange={(e) => handleChange(fd.slug, e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">{fd.name}</span>
          </label>
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(fd.slug, e.target.value)}
            className={baseClasses}
          >
            <option value="">Select...</option>
            {(fd.options?.choices || []).map((choice) => (
              <option key={choice} value={choice}>{choice}</option>
            ))}
          </select>
        );
      case 'multi_select':
        return (
          <div className="flex flex-wrap gap-2">
            {(fd.options?.choices || []).map((choice) => {
              const selected = Array.isArray(value) && value.includes(choice);
              return (
                <button
                  key={choice}
                  type="button"
                  onClick={() => {
                    const current = Array.isArray(value) ? value : [];
                    handleChange(fd.slug, selected ? current.filter(c => c !== choice) : [...current, choice]);
                  }}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${selected
                    ? 'bg-brand-100 border-brand-300 text-brand-700 dark:bg-brand-900/30 dark:border-brand-600 dark:text-brand-300'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400'
                  }`}
                >
                  {choice}
                </button>
              );
            })}
          </div>
        );
      case 'object_relationship':
        return (
          <RecordSearchField
            objectDefinitionId={fd.options?.related_object_definition_id}
            value={value}
            onChange={(v) => handleChange(fd.slug, v)}
            placeholder={`Select ${fd.name}...`}
          />
        );
      case 'user':
        return (
          <UserSearchField
            value={value}
            onChange={(v) => handleChange(fd.slug, v)}
            placeholder={`Select ${fd.name}...`}
          />
        );
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(fd.slug, e.target.value)}
            placeholder={fd.name}
            className={baseClasses}
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-xl mx-4 dark:bg-gray-800 ${useWideLayout ? 'w-full max-w-2xl' : 'w-full max-w-lg'}`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Record</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            <div className={useWideLayout ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
              {sortedFields.map((fd) => {
                const isFullWidth = fd.field_type === 'multi_select';
                return (
                  <div key={fd.id} className={useWideLayout && isFullWidth ? 'col-span-2' : ''}>
                    {fd.field_type !== 'boolean' && (
                      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                        {fd.name}
                        {fd.is_required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                    )}
                    {renderField(fd)}
                  </div>
                );
              })}
            </div>
            {sortedFields.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No fields defined for this object.</p>
            )}
          </div>

          {error && (
            <div className="px-6 pb-2">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateRecordModal;
export { RecordSearchField, UserSearchField };
